import { useCallback, useImperativeHandle, useRef } from "react";
import { validateRules } from "utils/form/validate";
import uuidv4 from "utils/uuidv4";
import useFieldReducer from "./useFieldReducer";

const useFormField = ({
  validationRef,
  validationRules = [],
  initialValue,
}) => {
  const [{ value, isValid, errorMessage }, dispatch] =
    useFieldReducer(initialValue);

  const updateValue = useCallback(
    (v) => {
      dispatch({ type: "updateValue", data: v });
    },
    [dispatch]
  );

  const setError = useCallback(
    (err) => {
      dispatch({ type: "setError", error: err });
    },
    [dispatch]
  );

  const validate = useCallback(() => {
    const err = validateRules(validationRules, value);
    if (err) {
      setError(err);
    }
    return err;
  }, [setError, validationRules, value]);

  const validationIdRef = useRef(uuidv4());

  const scrollIntoView = useCallback(() => {
    document
      .querySelector(`[data-validationid="${validationIdRef.current}"]`)
      ?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, []);

  useImperativeHandle(
    validationRef,
    () => ({
      validate,
      updateValue,
      setError,
      scrollIntoView,
      getData: () => value,
    }),
    [scrollIntoView, setError, updateValue, validate, value]
  );

  return {
    value,
    isValid,
    errorMessage,
    updateValue,
    setError,
    validationId: validationIdRef.current,
    validate,
  };
};

export default useFormField;
